import { Search, Filter, Star, Heart, MapPin, ArrowLeft, Package, Clock, Plus, User, MoreVertical, Edit, Pause, Play, Trash2, Eye, TrendingUp, Calendar, Map, Briefcase, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import AddFormToggle from "@/components/AddFormToggle";
import SearchAndFilters from "@/components/SearchAndFilters";
import LocationPreview from "@/components/LocationPreview";
import MapView from "@/components/MapView";
import { NotificationBell } from "@/components/NotificationSystem";
import { EmptyState } from "@/components/FeedbackStates";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

// Mock User's Services Data
const myServices = [
  {
    id: 201,
    title: "Personal Marketing Coaching",
    category: "Business & Consulting",
    price: "€85",
    priceType: "per uur",
    status: "active" as const,
    image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Help ondernemers hun marketing strategie te ontwikkelen en meer klanten te krijgen.",
    tags: ["Marketing", "Coaching", "Strategie"],
    createdAt: "2024-06-15",
    views: 89,
    bookings: 12,
    revenue: "€1.020"
  },
  {
    id: 202,
    title: "Content Creation Workshop",
    category: "Creative Services", 
    price: "€150",
    priceType: "per workshop",
    status: "draft" as const,
    image: "https://images.pexels.com/photos/4348401/pexels-photo-4348401.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Workshop over het maken van content die converteert voor social media en websites.",
    tags: ["Content", "Workshop", "Social Media"],
    createdAt: "2024-06-20",
    views: 0,
    bookings: 0,
    revenue: "€0"
  },
  {
    id: 203,
    title: "LinkedIn Profile Optimization",
    category: "Professional Services",
    price: "€75",
    priceType: "eenmalig",
    status: "paused" as const,
    image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Professionele LinkedIn profiel optimalisatie om meer opportunities te krijgen.",
    tags: ["LinkedIn", "Profiel", "Optimalisatie"],
    createdAt: "2024-06-10",
    views: 45,
    bookings: 6,
    revenue: "€450"
  }
];

// Mock User's Products Data
const myProducts = [
  {
    id: 301,
    title: "Social Media Mastery E-book",
    category: "Digitale Producten",
    price: "€35",
    status: "active" as const,
    image: "https://images.pexels.com/photos/267389/pexels-photo-267389.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Complete gids voor social media marketing met praktische tips en templates.",
    tags: ["E-book", "Social Media", "Marketing"],
    createdAt: "2024-06-05",
    views: 156,
    orders: 23,
    revenue: "€805"
  },
  {
    id: 302,
    title: "Instagram Template Pack",
    category: "Digitale Producten",
    price: "€25",
    status: "active" as const,
    image: "https://images.pexels.com/photos/196645/pexels-photo-196645.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "30 professionele Instagram post templates voor ondernemers.",
    tags: ["Templates", "Instagram", "Design"],
    createdAt: "2024-06-01",
    views: 203,
    orders: 41,
    revenue: "€1.025"
  },
  {
    id: 303,
    title: "Business Goal Planner",
    category: "Business Tools",
    price: "€45",
    status: "draft" as const,
    image: "https://images.pexels.com/photos/6686445/pexels-photo-6686445.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Fysieke planner voor het stellen en behalen van business doelen.",
    tags: ["Planner", "Goals", "Business"],
    createdAt: "2024-06-18",
    views: 12,
    orders: 0,
    revenue: "€0"
  }
];

// Mock Services Data
const mockServices = [
  {
    id: 1,
    title: "Marketing Strategy Consulting",
    provider: "Sarah van Dijk",
    category: "Business & Consulting",
    price: "€125",
    priceType: "per uur",
    rating: 4.9,
    reviews: 34,
    image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Brussel",
    fullAddress: "Regentschapsstraat 58, 1000 Brussel, België",
    serviceType: "both" as const,
    description: "Boost je bedrijf met professionele marketing strategieën en groeiplannen op maat.",
    tags: ["Marketing", "Strategie", "Online"]
  },
  {
    id: 2,
    title: "Professional Photography",
    provider: "Luna Fotografie",
    category: "Creative Services",
    price: "€200",
    priceType: "per shoot",
    rating: 5.0,
    reviews: 67,
    image: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Antwerpen",
    fullAddress: "Meir 45, 2000 Antwerpen, België",
    serviceType: "in-person" as const,
    description: "Professionele portret- en bedrijfsfotografie die jouw persoonlijkheid en merk laat stralen.",
    tags: ["Fotografie", "Portretten", "Business"]
  },
  {
    id: 3,
    title: "Website Development",
    provider: "Maya Tech Solutions",
    category: "Tech & Digital",
    price: "€75",
    priceType: "per uur",
    rating: 4.8,
    reviews: 42,
    image: "https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Gent",
    fullAddress: "Korenlei 12, 9000 Gent, België",
    serviceType: "both" as const,
    description: "Moderne, responsive websites die converteren. Van concept tot live website.",
    tags: ["Webdesign", "Development", "E-commerce"]
  },
  {
    id: 4,
    title: "Content Writing & Copywriting",
    provider: "Crystal Schrijft",
    category: "Creative Services",
    price: "€60",
    priceType: "per uur",
    rating: 4.7,
    reviews: 89,
    image: "https://images.pexels.com/photos/4348401/pexels-photo-4348401.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Online",
    fullAddress: "",
    serviceType: "online" as const,
    description: "Krachtige teksten die verkopen. Van websiteteksten tot social media content.",
    tags: ["Content", "SEO", "Social Media"]
  },
  {
    id: 5,
    title: "Personal Branding Coach",
    provider: "Emma Brand Expert",
    category: "Business & Consulting",
    price: "€95",
    priceType: "per sessie",
    rating: 4.9,
    reviews: 28,
    image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Leuven",
    fullAddress: "Bondgenotenlaan 15, 3000 Leuven, België",
    serviceType: "both" as const,
    description: "Ontdek en versterk je unieke personal brand. Van LinkedIn tot netwerken.",
    tags: ["Branding", "LinkedIn", "Netwerken"]
  },
  {
    id: 6,
    title: "Virtual Assistant Services",
    provider: "Iris Administratie",
    category: "Professional Services",
    price: "€35",
    priceType: "per uur",
    rating: 4.8,
    reviews: 156,
    image: "https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Brugge",
    fullAddress: "Markt 20, 8000 Brugge, België",
    serviceType: "online" as const,
    description: "Professionele administratieve ondersteuning. Email, planning, data-entry en meer.",
    tags: ["Administratie", "Planning", "Email"]
  }
];

// Mock Products Data
const mockProducts = [
  {
    id: 101,
    title: "Marketing Mastery E-book",
    provider: "Sarah van Dijk",
    category: "Digitale Producten",
    price: "€29",
    originalPrice: "€39",
    rating: 4.9,
    reviews: 156,
    image: "https://images.pexels.com/photos/4348401/pexels-photo-4348401.jpeg?auto=compress&cs=tinysrgb&w=400",
    inStock: true,
    deliveryTime: "Direct download",
    description: "Complette gids voor succesvolle marketing strategieën. 150+ pagina's met praktische tips.",
    tags: ["E-book", "Marketing", "PDF"]
  },
  {
    id: 102,
    title: "Logo Design Pakket",
    provider: "Nina Design Studio",
    category: "Creative Producten",
    price: "€150",
    rating: 5.0,
    reviews: 43,
    image: "https://images.pexels.com/photos/196645/pexels-photo-196645.jpeg?auto=compress&cs=tinysrgb&w=400",
    inStock: true,
    deliveryTime: "3-5 werkdagen",
    description: "Professioneel logo design met 3 concepten, onbeperkte revisies en alle bestandsformaten.",
    tags: ["Logo", "Branding", "Custom"]
  },
  {
    id: 103,
    title: "Social Media Templates Pack",
    provider: "Lotte Social",
    category: "Digitale Producten",
    price: "€25",
    rating: 4.8,
    reviews: 231,
    image: "https://images.pexels.com/photos/267389/pexels-photo-267389.jpeg?auto=compress&cs=tinysrgb&w=400",
    inStock: true,
    deliveryTime: "Direct download",
    description: "50+ Instagram post templates + 20 Story templates. Volledig aanpasbaar in Canva.",
    tags: ["Templates", "Instagram", "Canva"]
  },
  {
    id: 104,
    title: "Business Planner 2024",
    provider: "Emma Brand Expert",
    category: "Business Tools",
    price: "€35",
    rating: 4.7,
    reviews: 89,
    image: "https://images.pexels.com/photos/6686445/pexels-photo-6686445.jpeg?auto=compress&cs=tinysrgb&w=400",
    inStock: true,
    deliveryTime: "2-3 werkdagen",
    description: "Fysieke planner met goal setting, maandoverzichten en growth tracking. A5 formaat.",
    tags: ["Planner", "Goals", "Fysiek"]
  },
  {
    id: 105,
    title: "Handgemaakte Soja Kaarsen Set",
    provider: "Luna Handmade",
    category: "Lifestyle Producten",
    price: "€45",
    rating: 4.9,
    reviews: 67,
    image: "https://images.pexels.com/photos/1123262/pexels-photo-1123262.jpeg?auto=compress&cs=tinysrgb&w=400",
    inStock: true,
    deliveryTime: "1-2 werkdagen",
    description: "Set van 3 luxe soja kaarsen met natuurlijke geuren. 40+ branduren per kaars.",
    tags: ["Kaarsen", "Handgemaakt", "Natuurlijk"]
  },
  {
    id: 106,
    title: "Website Template Collection",
    provider: "Maya Tech Solutions",
    category: "Digitale Producten",
    price: "€89",
    originalPrice: "€129",
    rating: 4.8,
    reviews: 34,
    image: "https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg?auto=compress&cs=tinysrgb&w=400",
    inStock: true,
    deliveryTime: "Direct download",
    description: "10 professionele website templates. WordPress & HTML versies. Inclusief setup guide.",
    tags: ["Website", "Templates", "WordPress"]
  }
];

// Mock Jobs Data
const mockJobs = [
  {
    id: 401,
    title: "Mindfulness & Wellness Coach",
    company: "Conscious Living Collective",
    category: "Coaching & Wellness",
    salary: "€35-45",
    salaryType: "per uur",
    workType: "Part-time",
    location: "Amsterdam",
    locationType: "Hybrid",
    rating: 4.8,
    reviews: 12,
    image: "https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Werk als mindfulness coach in een bewuste community. Begeleid vrouwen op hun wellness journey.",
    tags: ["Mindfulness", "Coaching", "Wellness"],
    postedDate: "2 dagen geleden",
    applicationDeadline: "15 januari 2025",
    verified: true
  },
  {
    id: 402,
    title: "Creative Content Manager",
    company: "Women's Empowerment Hub",
    category: "Marketing & Content",
    salary: "€3.500-4.200",
    salaryType: "per maand",
    workType: "Full-time",
    location: "Brussels",
    locationType: "Hybrid",
    rating: 4.9,
    reviews: 28,
    image: "https://images.pexels.com/photos/3184306/pexels-photo-3184306.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Leid het content team en creëer inspirerende verhalen die vrouwen empoweren.",
    tags: ["Content", "Marketing", "Social Media"],
    postedDate: "1 week geleden",
    applicationDeadline: "20 januari 2025",
    verified: true
  },
  {
    id: 403,
    title: "UX Designer - Women's Health App",
    company: "HealthTech for Women",
    category: "Tech & Design",
    salary: "€4.000-5.500",
    salaryType: "per maand",
    workType: "Full-time",
    location: "Antwerp",
    locationType: "Remote",
    rating: 4.7,
    reviews: 18,
    image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Ontwerp user experiences voor een revolutionaire women's health app.",
    tags: ["UX Design", "Health Tech", "Remote"],
    postedDate: "3 dagen geleden",
    applicationDeadline: "25 januari 2025",
    verified: true
  },
  {
    id: 404,
    title: "Business Development Manager",
    company: "Female Founders Network",
    category: "Business & Strategy",
    salary: "€4.500-6.000",
    salaryType: "per maand",
    workType: "Full-time",
    location: "Ghent",
    locationType: "In-person",
    rating: 4.8,
    reviews: 22,
    image: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Ontwikkel partnerships en groei strategieën voor een netwerk van vrouwelijke ondernemers.",
    tags: ["Business Development", "Strategy", "Networking"],
    postedDate: "5 dagen geleden",
    applicationDeadline: "30 januari 2025",
    verified: true
  },
  {
    id: 405,
    title: "Sustainable Fashion Consultant",
    company: "EcoFashion Collective",
    category: "Fashion & Sustainability",
    salary: "€40-60",
    salaryType: "per uur",
    workType: "Freelance",
    location: "Online",
    locationType: "Remote",
    rating: 4.9,
    reviews: 31,
    image: "https://images.pexels.com/photos/3184319/pexels-photo-3184319.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Adviseer fashion brands over duurzaamheidsstrategieën en ethische productie.",
    tags: ["Fashion", "Sustainability", "Consulting"],
    postedDate: "1 dag geleden",
    applicationDeadline: "10 februari 2025",
    verified: true
  },
  {
    id: 406,
    title: "Data Scientist - Gender Analytics",
    company: "Equality Research Institute",
    category: "Data & Research",
    salary: "€5.000-7.000",
    salaryType: "per maand",
    workType: "Full-time",
    location: "Brussels",
    locationType: "Hybrid",
    rating: 4.6,
    reviews: 15,
    image: "https://images.pexels.com/photos/3184287/pexels-photo-3184287.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Analyseer data om gender equality in de workplace te bevorderen.",
    tags: ["Data Science", "Analytics", "Research"],
    postedDate: "4 dagen geleden",
    applicationDeadline: "5 februari 2025",
    verified: true
  }
];

const jobCategories = [
  "Alle categorieën",
  "Coaching & Wellness",
  "Marketing & Content",
  "Tech & Design",
  "Business & Strategy",
  "Fashion & Sustainability",
  "Data & Research",
  "Education & Training"
];
const serviceCategories = [
  "Alle categorieën",
  "Business & Consulting", 
  "Creative Services",
  "Tech & Digital",
  "Professional Services",
  "Lifestyle & Wellness",
  "Education & Training"
];

const productCategories = [
  "Alle categorieën",
  "Digitale Producten",
  "Creative Producten", 
  "Business Tools",
  "Lifestyle Producten",
  "Beauty & Wellness",
  "Fashion & Accessoires"
];

const WOMPlaza = () => {
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState<'browse' | 'mijn-aanbod' | 'toevoegen'>('browse');
  const [activeBrowseTab, setActiveBrowseTab] = useState<'services' | 'products' | 'jobs'>('services');
  const [activeMyTab, setActiveMyTab] = useState<'services' | 'products'>('services');
  const [favorites, setFavorites] = useState<number[]>([]);
  const [showMapView, setShowMapView] = useState(false);
  const [currentTab, setCurrentTab] = useState<'services' | 'products' | 'jobs'>('services');
  
  // Smart Filters State
  const [filters, setFilters] = useState({
    search: '',
    category: 'Alle categorieën',
    priceRange: [0, 500] as [number, number],
    minRating: 0,
    locations: [] as string[],
    availability: [] as string[],
    sortBy: 'popularity',
    distance: 0,
    userLocation: ''
  });

  const toggleFavorite = (itemId: number) => {
    setFavorites(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Reset filters when switching main tabs
  const handleMainTabSwitch = (tab: 'browse' | 'mijn-aanbod' | 'toevoegen') => {
    setActiveMainTab(tab);
    setFilters({
      search: '',
      category: 'Alle categorieën',
      priceRange: [0, 500],
      minRating: 0,
      locations: [],
      availability: [],
      sortBy: 'popularity',
      distance: 0,
      userLocation: ''
    });
  };

  // Reset filters when switching browse tabs
  const handleBrowseTabSwitch = (tab: 'services' | 'products' | 'jobs') => {
    setActiveBrowseTab(tab);
    setFilters(prev => ({
      ...prev,
      category: 'Alle categorieën',
      search: '',
      locations: [],
      availability: [],
      distance: 0,
      userLocation: ''
    }));
  };

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  // Smart filtering and sorting logic
  const filteredAndSortedItems = useMemo(() => {
    const currentData = activeBrowseTab === 'services' ? mockServices : 
                       activeBrowseTab === 'products' ? mockProducts : mockJobs;
    // Apply filters
    let filtered = currentData.filter(item => {
      // Search filter
      const searchMatch = !filters.search || 
        item.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        ('provider' in item ? item.provider.toLowerCase().includes(filters.search.toLowerCase()) : 
         'company' in item ? item.company.toLowerCase().includes(filters.search.toLowerCase()) : true) ||
        item.description.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(filters.search.toLowerCase()));

      // Category filter
      const categoryMatch = filters.category === 'Alle categorieën' || 
        item.category === filters.category;

      // Price range filter
      const price = activeBrowseTab === 'jobs' && 'salary' in item ? 
        parseFloat(item.salary.split('-')[0].replace('€', '').replace('.', '')) :
        parseFloat(item.price.replace('€', ''));
      const priceMatch = price >= filters.priceRange[0] && 
        (filters.priceRange[1] >= 500 || price <= filters.priceRange[1]);

      // Rating filter
      const ratingMatch = filters.minRating === 0 || item.rating >= filters.minRating;

      // Location filter (updated for Belgian cities and jobs)
      const locationMatch = activeBrowseTab === 'products' || 
        filters.locations.length === 0 ||
        filters.locations.some(loc => {
          const itemLocation = 'location' in item ? item.location : '';
          return (
            itemLocation === loc || 
            (loc === 'Online' && itemLocation === 'Online') ||
            (loc === 'Heel België' && itemLocation !== 'Online') ||
            (loc === 'Bij mij in de buurt' && filters.userLocation && filters.distance > 0)
          );
        });

      // Distance filter - simplified for jobs (they can be remote/hybrid)
      const distanceMatch = activeBrowseTab === 'jobs' || 
        !filters.userLocation || filters.distance === 0 || 
        activeBrowseTab === 'products' ||
        !('location' in item) ||
        item.location === 'Online' ||
        // Belgian regional clustering logic (same as before)
        (() => {
          const userCity = filters.userLocation.toLowerCase();
          const itemLocation = item.location.toLowerCase();

          // Always show online services and remote jobs
          if (item.location === 'Online' || 
              (activeBrowseTab === 'jobs' && 'locationType' in item && item.locationType === 'Remote')) {
            return true;
          }
          // If no distance set, show all
          if (filters.distance === 0) return true;
          // Belgian Regional Clusters - comprehensive mapping
          const belgianRegions = {
            // Brussels Capital Region + Flemish/Walloon Brabant
            'brussels': [
              'brussel', 'brussels', 'anderlecht', 'ixelles', 'uccle', 'schaerbeek', 'etterbeek',
              'forest', 'jette', 'koekelberg', 'molenbeek', 'saint-gilles', 'saint-josse',
              'woluwe', 'evere', 'ganshoren', 'berchem-sainte-agathe',
              // Flemish Brabant around Brussels
              'vilvoorde', 'machelen', 'zaventem', 'diegem', 'strombeek-bever', 'grimbergen',
              'meise', 'wolvertem', 'hofstade', 'elewijt', 'kapelle-op-den-bos',
              // Walloon Brabant around Brussels  
              'ottignies', 'louvain-la-neuve', 'wavre', 'braine-l\'alleud', 'waterloo',
              'rixensart', 'la-hulpe', 'genval', 'overijse', 'hoeilaart', 'tervuren'
            ],
            // Antwerp Region + surrounding municipalities
            'antwerp': [
              'antwerpen', 'anvers', 'amsterdam', 'berchem', 'borgerhout', 'deurne', 'hoboken', 'merksem',
              'wilrijk', 'edegem', 'hove', 'boechout', 'mortsel', 'kontich', 'aartselaar',
              'mechelen', 'malines', 'heist-op-den-berg', 'lier', 'duffel', 'putte',
              'boom', 'niel', 'schelle', 'hemiksem', 'rumst', 'walem', 'sint-katelijne-waver',
              'turnhout', 'geel', 'mol', 'herentals', 'westerlo', 'olen', 'vorselaar'
            ],
            // Ghent Region + East Flanders
            'ghent': [
              'gent', 'gand', 'ghent', 'gentbrugge', 'ledeberg', 'mariakerke', 'drongen', 'afsnee',
              'sint-denijs-westrem', 'zwijnaarde', 'oostakker', 'destelbergen', 'melle',
              'merelbeke', 'gontrode', 'lochristi', 'zele', 'wachtebeke', 'zelzate',
              'aalst', 'alost', 'dendermonde', 'ninove', 'geraardsbergen', 'herzele',
              'sint-niklaas', 'temse', 'lokeren', 'beveren', 'stekene', 'waasmunster'
            ],
            // Bruges Region + West Flanders
            'bruges': [
              'brugge', 'bruges', 'sint-michiels', 'sint-pieters', 'assebroek', 'sint-andries',
              'oostende', 'ostend', 'bredene', 'de-haan', 'blankenberge', 'zeebrugge',
              'knokke-heist', 'damme', 'sluis', 'maldegem', 'eeklo',
              'kortrijk', 'courtrai', 'kuurne', 'harelbeke', 'deerlijk', 'zwevegem',
              'waregem', 'wielsbeke', 'oostrozebeke', 'izegem', 'roeselare', 'roulers',
              'tielt', 'pittem', 'ardooie', 'meulebeke', 'dentergem'
            ],
            // Leuven Region + Flemish Brabant
            'leuven': [
              'leuven', 'louvain', 'heverlee', 'kessel-lo', 'wilsele', 'wijgmaal',
              'herent', 'korbeek-lo', 'bierbeek', 'bertem', 'oud-heverlee',
              'tienen', 'tirlemont', 'landen', 'zoutleeuw', 'hoegaarden', 'glabbeek',
              'diest', 'aarschot', 'scherpenheuvel-zichem', 'bekkevoort', 'begijnendijk',
              'rotselaar', 'keerbergen', 'haacht', 'boortmeerbeek', 'kampenhout'
            ],
            // Liège Region + Wallonia East
            'liege': [
              'liège', 'luik', 'seraing', 'ans', 'saint-nicolas', 'grâce-hollogne',
              'flémalle', 'engis', 'amay', 'huy', 'hoei', 'wanze', 'verlaine',
              'verviers', 'dison', 'pepinster', 'theux', 'spa', 'stavelot',
              'eupen', 'malmédy', 'saint-vith', 'bütgenbach', 'waimes',
              'herstal', 'oupeye', 'visé', 'dalhem', 'aubel', 'plombières'
            ],
            // Charleroi Region + Hainaut Center
            'charleroi': [
              'charleroi', 'marcinelle', 'montignies-sur-sambre', 'gilly', 'ransart',
              'jumet', 'lodelinsart', 'goutroux', 'dampremy', 'marchienne-au-pont',
              'châtelet', 'farciennes', 'aiseau-presles', 'pont-à-celles', 'les-bons-villers',
              'fleurus', 'gerpinnes', 'ham-sur-heure-nalinnes', 'thuin', 'lobbes',
              'fontaine-l\'évêque', 'courcelles', 'chapelle-lez-herlaimont'
            ],
            // Mons Region + Western Hainaut  
            'mons': [
              'mons', 'bergen', 'cuesmes', 'jemappes', 'nimy', 'saint-denis',
              'quaregnon', 'frameries', 'boussu', 'colfontaine', 'dour', 'quiévrain',
              'la-louvière', 'soignies', 'écaussinnes', 'le-rœulx', 'manage',
              'seneffe', 'braine-le-comte', 'enghien', 'edingen', 'silly',
              'tournai', 'doornik', 'antoing', 'rumes', 'brunehaut', 'celles'
            ],
            // Namur Region + Central Wallonia
            'namur': [
              'namur', 'namen', 'jambes', 'salzinnes', 'champion', 'temploux',
              'malonne', 'suarlée', 'flawinne', 'bouge', 'belgrade', 'cognelée',
              'dinant', 'ciney', 'rochefort', 'marche-en-famenne', 'durbuy',
              'somme-leuze', 'hotton', 'rendeux', 'tenneville', 'wellin',
              'gembloux', 'eghezée', 'la-bruyère', 'sombreffe', 'jodoigne', 'perwez'
            ]
          };
          // Extract city from user location (handle postal codes and complex names)
          const extractUserCity = (location: string): string => {
            const cleanLocation = location.toLowerCase().trim();
            // Remove postal codes (4 digits at start)  
            const withoutPostal = cleanLocation.replace(/^\d{4}\s*/, '');
            // Handle special cases
            if (withoutPostal.includes('anderlecht')) return 'anderlecht';
            if (withoutPostal.includes('brussel') || withoutPostal.includes('brussels')) return 'brussel';
            if (withoutPostal.includes('antwerpen') || withoutPostal.includes('anvers') || withoutPostal.includes('amsterdam')) return 'antwerp';
            if (withoutPostal.includes('gent') || withoutPostal.includes('gand') || withoutPostal.includes('ghent')) return 'ghent';
            if (withoutPostal.includes('brugge') || withoutPostal.includes('bruges')) return 'bruges';
            if (withoutPostal.includes('leuven') || withoutPostal.includes('louvain')) return 'leuven';
            if (withoutPostal.includes('liège') || withoutPostal.includes('luik')) return 'liege';
            if (withoutPostal.includes('charleroi')) return 'charleroi';
            if (withoutPostal.includes('mons') || withoutPostal.includes('bergen')) return 'mons';
            if (withoutPostal.includes('namur') || withoutPostal.includes('namen')) return 'namur';
            // Default: take first word after postal code removal
            return withoutPostal.split(' ')[0] || cleanLocation;
          };
          const userCityExtracted = extractUserCity(userCity);
          const itemCityExtracted = extractUserCity(itemLocation);
          // Find which region the user and item belong to
          const findRegion = (city: string): string | null => {
            for (const [regionName, cities] of Object.entries(belgianRegions)) {
              if (cities.some(regionCity => 
                city.includes(regionCity) || regionCity.includes(city)
              )) {
                return regionName;
              }
            }
            return null;
          };
          const userRegion = findRegion(userCityExtracted);
          const itemRegion = findRegion(itemCityExtracted);
          // If both are in the same region, use regional distance limits
          if (userRegion && itemRegion && userRegion === itemRegion) {
            const regionDistanceLimits = {
              'brussels': 25, // Brussels region spans ~25km
              'antwerp': 30,  // Antwerp region
              'ghent': 25,    // Ghent region  
              'bruges': 35,   // West Flanders is more spread
              'leuven': 20,   // Flemish Brabant (compact)
              'liege': 30,    // Liège region
              'charleroi': 20, // Charleroi basin
              'mons': 25,     // Mons region
              'namur': 35     // Namur province (more rural)
            };
            const maxRegionalDistance = regionDistanceLimits[userRegion as keyof typeof regionDistanceLimits] || 30;
            return filters.distance >= Math.min(15, maxRegionalDistance); // At least 15km for regional matching
          }
          // Cross-region distance calculation (existing logic)
          const cityDistances: { [key: string]: { [key: string]: number } } = {
            'brussels': { 'antwerp': 45, 'ghent': 55, 'bruges': 95, 'leuven': 25, 'liege': 95, 'charleroi': 60, 'mons': 65, 'namur': 65 },
            'antwerp': { 'brussels': 45, 'ghent': 60, 'bruges': 85, 'leuven': 70, 'liege': 120, 'charleroi': 105, 'mons': 110, 'namur': 110 },
            'ghent': { 'brussels': 55, 'antwerp': 60, 'bruges': 45, 'leuven': 85, 'liege': 150, 'charleroi': 115, 'mons': 85, 'namur': 120 },
            'bruges': { 'brussels': 95, 'antwerp': 85, 'ghent': 45, 'leuven': 120, 'liege': 195, 'charleroi': 140, 'mons': 105, 'namur': 165 },
            'leuven': { 'brussels': 25, 'antwerp': 70, 'ghent': 85, 'bruges': 120, 'liege': 75, 'charleroi': 85, 'mons': 90, 'namur': 75 },
            'liege': { 'brussels': 95, 'antwerp': 120, 'ghent': 150, 'bruges': 195, 'leuven': 75, 'charleroi': 85, 'mons': 125, 'namur': 65 },
            'charleroi': { 'brussels': 60, 'antwerp': 105, 'ghent': 115, 'bruges': 140, 'leuven': 85, 'liege': 85, 'mons': 25, 'namur': 45 },
            'mons': { 'brussels': 65, 'antwerp': 110, 'ghent': 85, 'bruges': 105, 'leuven': 90, 'liege': 125, 'charleroi': 25, 'namur': 70 },
            'namur': { 'brussels': 65, 'antwerp': 110, 'ghent': 120, 'bruges': 165, 'leuven': 75, 'liege': 65, 'charleroi': 45, 'mons': 70 }
          };
          // Use region-based lookup or fallback to city name
          const userLookup = userRegion || userCityExtracted;
          const itemLookup = itemRegion || itemCityExtracted;
          const distance = cityDistances[userLookup]?.[itemLookup] || 
                          cityDistances[itemLookup]?.[userLookup] || 
                          999; // Default to very far if not found
          return distance <= filters.distance;
        })();

      // Availability filter (mock logic)
      const availabilityMatch = filters.availability.length === 0 || true; // Simplified for demo

      return searchMatch && categoryMatch && priceMatch && ratingMatch && locationMatch && distanceMatch && availabilityMatch;
    });

    // Apply sorting
    switch (filters.sortBy) {
      case 'distance':
        // Mock distance sorting - in real app would calculate actual distances
        filtered.sort((a, b) => {
          // Prioritize items with locations over online-only
          const aLocation = 'location' in a ? a.location : '';
          const bLocation = 'location' in b ? b.location : '';
          if (aLocation === 'Online' && bLocation !== 'Online') return 1;
          if (bLocation === 'Online' && aLocation !== 'Online') return -1;
          // Mock distance calculation based on alphabetical order for demo
          return aLocation.localeCompare(bLocation);
        });
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = activeBrowseTab === 'jobs' && 'salary' in a ? 
            parseFloat(a.salary.split('-')[0].replace('€', '').replace('.', '')) :
            parseFloat(a.price.replace('€', ''));
          const priceB = activeBrowseTab === 'jobs' && 'salary' in b ? 
            parseFloat(b.salary.split('-')[0].replace('€', '').replace('.', '')) :
            parseFloat(b.price.replace('€', ''));
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = activeBrowseTab === 'jobs' && 'salary' in a ? 
            parseFloat(a.salary.split('-')[0].replace('€', '').replace('.', '')) :
            parseFloat(a.price.replace('€', ''));
          const priceB = activeBrowseTab === 'jobs' && 'salary' in b ? 
            parseFloat(b.salary.split('-')[0].replace('€', '').replace('.', '')) :
            parseFloat(b.price.replace('€', ''));
          return priceB - priceA;
        });
        break;
      case 'reviews':
        filtered.sort((a, b) => b.reviews - a.reviews);
        break;
      case 'newest':
        // Mock newest sort by id (higher id = newer)
        filtered.sort((a, b) => b.id - a.id);
        break;
      case 'popularity':
      default:
        // Mock popularity sort by reviews count
        filtered.sort((a, b) => b.reviews - a.reviews);
        break;
    }

    return filtered;
  }, [activeBrowseTab, filters]);

  const handleStatusChange = (itemId: number, newStatus: string, type: 'service' | 'product') => {
    // Here we would update the status in real app
    toast.success(`${type === 'service' ? 'Dienst' : 'Product'} status gewijzigd naar ${newStatus}`);
    console.log(`Updating ${type} ${itemId} status to ${newStatus}`);
  };

  const handleEdit = (itemId: number, type: 'service' | 'product') => {
    // This will navigate to edit form with pre-filled data
    const editUrl = type === 'service' ? `/add-service/edit/${itemId}` : `/add-product/edit/${itemId}`;
    navigate(editUrl);
    toast.info(`Bewerken ${type === 'service' ? 'dienst' : 'product'}...`);
  };

  const handleDelete = (itemId: number, type: 'service' | 'product') => {
    // Here we would delete the item in real app
    toast.success(`${type === 'service' ? 'Dienst' : 'Product'} verwijderd`);
    console.log(`Deleting ${type} ${itemId}`);
  };

  const myServicesActive = myServices.filter(s => s.status === 'active').length;
  const myProductsActive = myProducts.filter(p => p.status === 'active').length;
  const totalListings = myServices.length + myProducts.length;
  const totalActive = myServicesActive + myProductsActive;
  const totalViews = [...myServices, ...myProducts].reduce((sum, item) => sum + item.views, 0);
  const totalRevenue = [...myServices, ...myProducts].reduce((sum, item) => {
    const revenue = parseFloat(item.revenue.replace('€', '').replace('.', ''));
    return sum + revenue;
  }, 0);

  const currentMyData = activeMyTab === 'services' ? myServices : myProducts;
  const currentCategories = activeBrowseTab === 'services' ? serviceCategories : 
                           activeBrowseTab === 'products' ? productCategories : jobCategories;

  return (
    <div className="w-full max-w-full overflow-x-hidden" style={{ minHeight: '100vh', backgroundColor: '#f6f1eb' }}>
      {/* Header with Search */}
      <div className="sticky top-0 z-50 w-full" style={{ backgroundColor: '#fbf8f4', borderBottom: '1px solid #efe7dd' }}>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-4">
          {/* Header Row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="p-2 hover:bg-stone-100 flex-shrink-0"
                style={{ color: '#837069' }}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-semibold truncate" style={{ color: '#837069' }}>WOM Plaza</h1>
                <p className="text-xs sm:text-sm truncate" style={{ color: '#b6a092' }}>
                  Diensten, producten & vacatures van vrouwen in België
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <NotificationBell />
            </div>
          </div>

          {/* WOM Plaza Header */}
          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
            <div className="text-center">
              <div className="mb-2">
                <h1 className="text-2xl sm:text-3xl font-light tracking-wider" style={{ color: '#837069' }}>
                  <span className="font-bold" style={{ color: '#a0897c' }}>W</span>
                  <span className="mx-1" style={{ color: '#d9cbbf' }}>·</span>
                  <span className="font-bold" style={{ color: '#a0897c' }}>O</span>
                  <span className="mx-1" style={{ color: '#d9cbbf' }}>·</span>
                  <span className="font-bold" style={{ color: '#a0897c' }}>M</span>
                  <span className="ml-3 font-bold" style={{ color: '#a0897c' }}>Plaza</span>
                </h1>
              </div>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Ontdek diensten, producten en vacatures van vrouwen in België
              </p>
            </div>
          </div>

          {/* Main Navigation Tabs */}
          <div className="w-full overflow-x-auto scrollbar-hide mb-4">
            <div className="flex items-center gap-1 rounded-lg p-1 min-w-max w-fit mx-auto" style={{ backgroundColor: '#efe7dd' }}>
              <button
                onClick={() => handleMainTabSwitch('browse')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm sm:text-base whitespace-nowrap ${
                  activeMainTab === 'browse' 
                    ? 'bg-white shadow-sm' 
                    : 'hover:bg-white/50'
                }`}
                style={{ color: activeMainTab === 'browse' ? '#837069' : '#b6a092' }}
              >
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">Ontdekken</span>
                <span className="sm:hidden">Zoeken</span>
              </button>
              <button
                onClick={() => handleMainTabSwitch('mijn-aanbod')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm sm:text-base whitespace-nowrap ${
                  activeMainTab === 'mijn-aanbod' 
                    ? 'bg-white shadow-sm' 
                    : 'hover:bg-white/50'
                }`}
                style={{ color: activeMainTab === 'mijn-aanbod' ? '#837069' : '#b6a092' }}
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Mijn Aanbod</span>
                <span className="sm:hidden">Mijn</span>
              </button>
              <button
                onClick={() => handleMainTabSwitch('toevoegen')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm sm:text-base whitespace-nowrap ${
                  activeMainTab === 'toevoegen' 
                    ? 'bg-white shadow-sm' 
                    : 'hover:bg-white/50'
                }`}
                style={{ color: activeMainTab === 'toevoegen' ? '#837069' : '#b6a092' }}
              >
                <Plus className="h-4 w-4" />
                Toevoegen
              </button>
            </div>
          </div>

          {/* Browse Tab Content */}
          {activeMainTab === 'browse' && (
            <>
              {/* Services/Products/Jobs Toggle */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                <div className="w-full sm:w-auto overflow-x-auto scrollbar-hide">
                  <div className="flex items-center gap-1 rounded-lg p-1 min-w-max" style={{ backgroundColor: '#efe7dd' }}>
                    <button
                      onClick={() => handleBrowseTabSwitch('services')}
                      className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm whitespace-nowrap ${
                        activeBrowseTab === 'services' 
                          ? 'bg-white shadow-sm' 
                          : 'hover:bg-white/50'
                      }`}
                      style={{ color: activeBrowseTab === 'services' ? '#837069' : '#b6a092' }}
                    >
                      <Clock className="h-4 w-4" />
                      Diensten
                    </button>
                    <button
                      onClick={() => handleBrowseTabSwitch('products')}
                      className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm whitespace-nowrap ${
                        activeBrowseTab === 'products' 
                          ? 'bg-white shadow-sm' 
                          : 'hover:bg-white/50'
                      }`}
                      style={{ color: activeBrowseTab === 'products' ? '#837069' : '#b6a092' }}
                    >
                      <Package className="h-4 w-4" />
                      Producten
                    </button>
                    <button
                      onClick={() => handleBrowseTabSwitch('jobs')}
                      className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm whitespace-nowrap ${
                        activeBrowseTab === 'jobs' 
                          ? 'bg-white shadow-sm' 
                          : 'hover:bg-white/50'
                      }`}
                      style={{ color: activeBrowseTab === 'jobs' ? '#837069' : '#b6a092' }}
                    >
                      <Briefcase className="h-4 w-4" />
                      Vacatures
                    </button>
                  </div>
                </div>

                {/* Map View Button - Only for services */}
                {activeBrowseTab === 'services' && (
                  <div className="w-full sm:w-auto">
                    <Button
                      onClick={() => setShowMapView(true)}
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto hover:bg-white/50 text-xs sm:text-sm"
                      style={{ borderColor: '#efe7dd', color: '#837069' }}
                    >
                      <Map className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Kaart Weergave</span>
                      <span className="sm:hidden">Kaart</span>
                    </Button>
                  </div>
                )}
              </div>
              {/* Advanced Search & Filters */}
              <div className="w-full">
                <SearchAndFilters
                  currentTab={activeBrowseTab}
                  categories={currentCategories}
                  onFiltersChange={handleFiltersChange}
                  resultsCount={filteredAndSortedItems.length}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content Area - Fixed for scrollability and responsive width */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-32">
        {filteredAndSortedItems.length === 0 && (
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-32">
            <h2 className="text-xl sm:text-2xl font-medium mb-3" style={{ color: '#837069' }}>
              Geen {activeBrowseTab === 'services' ? 'diensten' : 
                    activeBrowseTab === 'products' ? 'producten' : 'vacatures'} gevonden
            </h2>
            <p className="mb-6 max-w-md mx-auto px-4" style={{ color: '#b6a092' }}>
              Probeer je zoekopdracht aan te passen of andere filters te gebruiken. 
              Er zijn veel geweldige aanbiedingen van women on a mission!
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 justify-center px-4">
              <Button 
                variant="outline" 
                onClick={() => setFilters(prev => ({ ...prev, search: '', category: 'Alle categorieën' }))}
                className="hover:bg-white/50 w-full sm:w-auto"
                style={{ borderColor: '#efe7dd', color: '#837069' }}
              >
                Wis zoekopdracht
              </Button>
              <Button 
                onClick={() => handleBrowseTabSwitch(
                  activeBrowseTab === 'services' ? 'products' : 
                  activeBrowseTab === 'products' ? 'jobs' : 'services'
                )}
                className="hover:opacity-90 w-full sm:w-auto"
                style={{ backgroundColor: '#a0897c', color: 'white' }}
              >
                Bekijk {activeBrowseTab === 'services' ? 'Producten' : 
                        activeBrowseTab === 'products' ? 'Vacatures' : 'Diensten'}
              </Button>
            </div>
          </div>
        )}
        {activeMainTab === 'browse' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredAndSortedItems.map(item => (
              <div 
                key={item.id}
                className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1 cursor-pointer w-full"
                style={{ border: '1px solid #efe7dd' }}
                onClick={() => navigate(
                  activeBrowseTab === 'services' ? `/service/${item.id}` : 
                  activeBrowseTab === 'products' ? `/product/${item.id}` : 
                  `/job/${item.id}`
                )}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <h3 className="font-medium text-base sm:text-lg line-clamp-2 flex-1" style={{ color: '#837069' }}>{item.title}</h3>
                    <div className="text-right flex-shrink-0">
                      {activeBrowseTab === 'jobs' && 'salary' in item ? (
                        <>
                          <div className="font-semibold text-sm sm:text-base" style={{ color: '#a0897c' }}>{item.salary}</div>
                          <div className="text-xs" style={{ color: '#b6a092' }}>{item.salaryType}</div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold text-sm sm:text-base" style={{ color: '#a0897c' }}>{item.price}</div>
                          {'originalPrice' in item && item.originalPrice && (
                            <div className="text-xs line-through" style={{ color: '#d9cbbf' }}>{item.originalPrice}</div>
                          )}
                          {'priceType' in item && (
                            <div className="text-xs" style={{ color: '#b6a092' }}>{item.priceType}</div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <p className="text-sm mb-3 line-clamp-2" style={{ color: '#b6a092' }}>
                    {item.description}
                  </p>

                  <div className="flex items-center justify-between mb-3 gap-2">
                    <div className="text-sm font-medium truncate flex-1" style={{ color: '#837069' }}>
                      {activeBrowseTab === 'jobs' && 'company' in item ? item.company : 
                       'provider' in item ? item.provider : ''}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">{item.rating}</span>
                      <span className="text-xs" style={{ color: '#b6a092' }}>({item.reviews})</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {activeBrowseTab === 'jobs' && 'location' in item ? (
                        <div className="flex items-center gap-1 text-xs" style={{ color: '#b6a092' }}>
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{item.location}</span>
                          {'locationType' in item && (
                            <>
                              <span className="mx-1">•</span>
                              <span className="truncate">{item.locationType}</span>
                            </>
                          )}
                        </div>
                      ) : activeBrowseTab === 'services' && 'location' in item ? (
                        <LocationPreview
                          location={item.location}
                          address={item.fullAddress}
                          serviceType={item.serviceType}
                          compact={true}
                        />
                      ) : (
                        <div className="flex items-center gap-1 text-xs" style={{ color: '#b6a092' }}>
                          <Package className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{'deliveryTime' in item && item.deliveryTime}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {item.tags.slice(0, 2).map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs" style={{ backgroundColor: '#efe7dd', color: '#837069' }}>
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Enhanced badge for high-rated items and job-specific badges */}
                {item.rating >= 4.8 && (
                  <div className="absolute top-3 left-3">
                    <Badge className="text-white text-xs" style={{ backgroundColor: '#a0897c' }}>
                      Top Rated
                    </Badge>
                  </div>
                )}
                {activeBrowseTab === 'jobs' && 'verified' in item && item.verified && (
                  <div className="absolute top-3 left-3">
                    <Badge className="text-white text-xs bg-green-500">
                      Verified Company
                    </Badge>
                  </div>
                )}
                {activeBrowseTab === 'jobs' && 'workType' in item && item.workType === 'Remote' && (
                  <div className="absolute bottom-3 left-3">
                    <Badge className="text-white text-xs bg-blue-500">
                      Remote Work
                    </Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mijn Aanbod Content */}
      {activeMainTab === 'mijn-aanbod' && (
        <div className="space-y-6">
          {/* Stats Dashboard */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm" style={{ border: '1px solid #efe7dd' }}>
              <h3 className="text-sm font-medium mb-1" style={{ color: '#b6a092' }}>Totaal Aanbod</h3>
              <p className="text-2xl font-bold" style={{ color: '#837069' }}>{totalListings}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" style={{ border: '1px solid #efe7dd' }}>
              <h3 className="text-sm font-medium mb-1" style={{ color: '#b6a092' }}>Actief</h3>
              <p className="text-2xl font-bold" style={{ color: '#a0897c' }}>{totalActive}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" style={{ border: '1px solid #efe7dd' }}>
              <h3 className="text-sm font-medium mb-1" style={{ color: '#b6a092' }}>Weergaven</h3>
              <p className="text-2xl font-bold" style={{ color: '#837069' }}>{totalViews}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" style={{ border: '1px solid #efe7dd' }}>
              <h3 className="text-sm font-medium mb-1" style={{ color: '#b6a092' }}>Omzet</h3>
              <p className="text-2xl font-bold" style={{ color: '#a0897c' }}>€{totalRevenue.toLocaleString()}</p>
            </div>
          </div>

          {/* Services/Products Toggle */}
          <div className="flex items-center gap-1 rounded-lg p-1 w-fit" style={{ backgroundColor: '#efe7dd' }}>
            <button
              onClick={() => setActiveMyTab('services')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all text-sm ${
                activeMyTab === 'services' 
                  ? 'bg-white shadow-sm' 
                  : 'hover:bg-white/50'
              }`}
              style={{ color: activeMyTab === 'services' ? '#837069' : '#b6a092' }}
            >
              <Clock className="h-4 w-4" />
              Mijn Diensten ({myServices.length})
            </button>
            <button
              onClick={() => setActiveMyTab('products')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all text-sm ${
                activeMyTab === 'products' 
                  ? 'bg-white shadow-sm' 
                  : 'hover:bg-white/50'
              }`}
              style={{ color: activeMyTab === 'products' ? '#837069' : '#b6a092' }}
            >
              <Package className="h-4 w-4" />
              Mijn Producten ({myProducts.length})
            </button>
          </div>

          {/* My Items Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentMyData.map(item => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm overflow-hidden" style={{ border: '1px solid #efe7dd' }}>
                <div className="aspect-video bg-gray-100 bg-cover bg-center" style={{ backgroundImage: `url(${item.image})` }}>
                  <div className="flex items-start justify-between p-3">
                    <Badge 
                      variant={item.status === 'active' ? 'default' : item.status === 'draft' ? 'secondary' : 'outline'}
                      className="text-xs"
                      style={{ 
                        backgroundColor: item.status === 'active' ? '#a0897c' : 
                                        item.status === 'draft' ? '#efe7dd' : 'transparent',
                        color: item.status === 'active' ? 'white' : '#837069',
                        borderColor: item.status === 'paused' ? '#d9cbbf' : 'transparent'
                      }}
                    >
                      {item.status === 'active' ? 'Actief' : 
                       item.status === 'draft' ? 'Concept' : 'Gepauzeerd'}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 bg-white/80 hover:bg-white">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(item.id, activeMyTab === 'services' ? 'service' : 'product')}>
                          Bewerken
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(item.id, activeMyTab === 'services' ? 'service' : 'product')}>
                          Verwijderen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toevoegen Content */}
      {activeMainTab === 'toevoegen' && (
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-medium mb-3" style={{ color: '#837069' }}>
              Voeg je aanbod toe
            </h2>
            <p className="text-base" style={{ color: '#b6a092' }}>
              Kies wat je wilt toevoegen aan WOM Plaza
            </p>
          </div>
          
          <AddFormToggle />
        </div>
      )}
    </div>
  );
};

export default WOMPlaza;